class RotationSpace {
    constructor(camQuat, objQuat, upAxis) {
        const camLookDir = new THREE.Vector3(0, 0, -1).applyQuaternion(camQuat);
        const sideAxis = new THREE.Vector3().crossVectors(upAxis, camLookDir).normalize();

        if (sideAxis.lengthSq() < 0.5) {
            const camUpDir = new THREE.Vector3(0, 1, 0).applyQuaternion(camQuat);
            sideAxis.crossVectors(upAxis, camUpDir);

            if (upAxis.dot(camLookDir) > 0) {
                sideAxis.multiplyScalar(-1);
            }

            sideAxis.normalize();
        }

        const frontAxis = new THREE.Vector3().crossVectors(sideAxis, upAxis);
        const spaceMat = new THREE.Matrix4().set(
            sideAxis.x, upAxis.x, frontAxis.x, 0,
            sideAxis.y, upAxis.y, frontAxis.y, 0,
            sideAxis.z, upAxis.z, frontAxis.z, 0,
            0, 0, 0, 1
        );

        const spaceQuat = new THREE.Quaternion().setFromRotationMatrix(spaceMat);
        const spaceInvQuat = spaceQuat.clone().inverse();

        const insideObjQuat = objQuat.clone().premultiply(spaceInvQuat);
        const insideCamQuat = camQuat.clone().premultiply(spaceInvQuat);
        const insideCamLookDir = new THREE.Vector3(0, 0, -1).applyQuaternion(insideCamQuat);
        const insideCamUpDir = new THREE.Vector3(0, 1, 0).applyQuaternion(insideCamQuat);

        this._spaceQuat = spaceQuat
        this._spaceInvQuat = spaceInvQuat
        this._insideObjQuat = insideObjQuat;

        const angleV = Math.asin(Math.min(Math.max(-1, insideCamLookDir.y), 1));
        const insideCamUnrolledUpDir = new THREE.Vector3(0, Math.cos(angleV), -Math.sin(angleV));
        const angleRoll = Math.atan2(insideCamUpDir.x, insideCamUpDir.dot(insideCamUnrolledUpDir));

        [this._angleH, this._angleV, this._angleRoll] = [0, angleV, angleRoll];
    }

    get spaceQuaternion() { return this._spaceQuat; }
    get angleH() { return this._angleH; }
    get angleV() { return this._angleV; }
    get angleRoll() { return this._angleRoll; }
    set angleH(angle) { this._angleH = angle; }
    set angleV(angle) { this._angleV = Math.min(Math.max(-Math.PI * 0.499, angle), Math.PI * 0.499); }
    set angleRoll(angle) { this._angleRoll = angle; }

    getObjQuatWithRespectToCam(relObjQuat = new THREE.Quaternion()) {
        const finalCamEuler = new THREE.Euler(this._angleV, this._angleH + Math.PI, this._angleRoll, 'YXZ');
        const finalCamQuat = new THREE.Quaternion().setFromEuler(finalCamEuler);

        return relObjQuat.copy(finalCamQuat).inverse().multiply(this._insideObjQuat);
    }

    getDirsFromAngles(angleH, angleV, angleRoll) {
        const hSin = Math.sin(angleH), hCos = Math.cos(angleH);
        const vSin = Math.sin(angleV), vCos = Math.cos(angleV);
        const lookDir = new THREE.Vector3(hSin * vCos, vSin, hCos * vCos);
        const rollQuat = new THREE.Quaternion().setFromAxisAngle(lookDir, angleRoll);
        const upDir = new THREE.Vector3(-hSin * vSin, vCos, -hCos * vSin).applyQuaternion(rollQuat);
        const sideDir = new THREE.Vector3().crossVectors(upDir, lookDir);

        return [sideDir, upDir, lookDir];
    }

    objCamQuatChanged(newObjQuat, newCamQuat) {
        const spaceQuatFromObj = this._insideObjQuat.clone().inverse();
        const newSpaceQuat = spaceQuatFromObj.clone().premultiply(newObjQuat);
        const newSpaceInvQuat = newSpaceQuat.clone().inverse();
        const insideCamQuat = newCamQuat.clone().premultiply(newSpaceInvQuat);
        const upDir = new THREE.Vector3(0, 1, 0).applyQuaternion(insideCamQuat);
        const lookDir = new THREE.Vector3(0, 0, -1).applyQuaternion(insideCamQuat);
        const lookFlatDir = new THREE.Vector3().set(lookDir.x, 0, lookDir.z).normalize();

        if (lookFlatDir.lengthSq() >= 0.5) {
            const angleH = Math.atan2(lookFlatDir.x, lookFlatDir.z);
            const angleV = Math.asin(Math.min(Math.max(-1, lookDir.y), 1));
            const [unrolledSideDir, unrolledUpDir,] = this.getDirsFromAngles(angleH, angleV, 0);
            const angleRoll = Math.atan2(upDir.dot(unrolledSideDir), upDir.dot(unrolledUpDir));

            this._angleH = angleH;
            this._angleV = angleV;
            this._angleRoll = angleRoll;
        }
    }
}

export default RotationSpace;
export { RotationSpace };